import { ChangeEvent, FC, useCallback, useState } from 'react';

import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';

import { zIndex } from 'styles';

import { sendContactForm } from 'client';
import { validateEmail } from 'components/ContactForm/utils';
import {
  Button,
  EmailInput,
  EmailInputContainer,
  FormControlContainer,
  ModalContent,
} from 'components/Header/ContactModal.style';

type SubscribeModalProps = { onClose: () => void; isOpen: boolean };

export const SubscribeModal: FC<SubscribeModalProps> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(true);

  const handleClose = useCallback(() => {
    setEmail('');
    setIsValid(true);
    onClose();
  }, [onClose]);

  const onSubmit = useCallback(() => {
    if (!validateEmail(email)) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
    setIsLoading(true);

    sendContactForm(email, 'resources-subscribe')
      .then(() => {
        setIsLoading(false);
        handleClose();
      })
      .catch(() => {
        setIsLoading(false);
        setIsValid(false);
      });
  }, [email, handleClose]);

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.currentTarget.value);
  }, []);

  return (
    <Modal
      sx={{
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="subscribe-modal-title"
      aria-describedby="subscribe-modal-description"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        },
      }}
    >
      <ModalContent p={[3]}>
        <Typography id="subscribe-modal-title" variant="h6" component="h2">
          Subscribe
        </Typography>
        <Typography id="subscribe-modal-description" sx={{ mt: 2 }}>
          Stay updated with the latest articles, white papers, and news.
        </Typography>
        <FormControlContainer>
          <EmailInputContainer mt={[0]} mb={[0.75, 0]} mr={[0, 1]}>
            <EmailInput
              error={!isValid}
              disabled={isLoading}
              fullWidth
              value={email}
              placeholder="Email address"
              onChange={onChange}
              aria-describedby="subscribe-email-error"
              aria-invalid={!isValid}
            />
            {!isValid && (
              <span id="subscribe-email-error" className="sr-only">
                Please enter a valid email address.
              </span>
            )}
          </EmailInputContainer>
          <Button
            disabled={isLoading}
            size="large"
            onClick={onSubmit}
            aria-label="Subscribe to updates"
          >
            Subscribe
          </Button>
        </FormControlContainer>
      </ModalContent>
    </Modal>
  );
};
